import React from "react";
import { useState } from "react";
import { ChevronDown, Users, Calendar, Clock, DollarSign, Utensils, GraduationCap, Briefcase } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const Services = () => {
  const services = [
    {
      id: "private-dinners",
      title: "Private Dinner Parties",
      icon: <Users className="w-8 h-8" />,
      description: "Customized dining experience for 2-15 guests with personalized service.",
      features: [
        "Custom menu design based on preferences",
        "Professional table service",
        "Complete setup and cleanup",
        "Wine pairing recommendations"
      ],
      pricing: "Starting at $125 per person + ingredients"
    },
    {
      id: "meal-prep",
      title: "Meal Preparation Services",
      icon: <Utensils className="w-8 h-8" />,
      description: "Weekly meal prep with restaurant-quality dishes for your busy lifestyle.",
      features: [
        "Customized weekly meal plans",
        "Dietary restriction accommodation",
        "Vacuum-sealed packaging",
        "Reheating instructions provided"
      ],
      pricing: "Starting at $200 per week + ingredients"
    },
    {
      id: "cooking-classes",
      title: "Private Cooking Classes",
      icon: <GraduationCap className="w-8 h-8" />,
      description: "Learn professional techniques in hands-on cooking sessions.",
      features: [
        "Technique-focused instruction",
        "Recipe cards to take home",
        "Small group settings (2-6 people)",
        "All ingredients and equipment provided"
      ],
      pricing: "Starting at $150 per person (2-hour session)"
    },
    {
      id: "large-gatherings",
      title: "Large Gatherings & Events",
      icon: <Briefcase className="w-8 h-8" />,
      description: "Impress clients and colleagues with exceptional catering services.",
      features: [
        "Executive dining experiences",
        "Team building cooking events",
        "Business lunch catering",
        "Holiday party menus"
      ],
      pricing: "Custom pricing based on event size and requirements"
    }
  ];

  const menuSamples = [
    {
      id: "traditional-euro",
      title: "Traditional European",
      description: "Classic European flavors with modern presentation",
      courses: [
        {
          course: "Appetizer",
          dish: "Duck Liver Pâté with Fig Compote",
          description: "Smooth pâté served with house-made brioche"
        },
        {
          course: "Soup",
          dish: "French Onion Soup Gratinée",
          description: "Traditional recipe with Gruyère cheese"
        },
        {
          course: "Main",
          dish: "Coq au Vin with Herbed Potatoes",
          description: "Braised chicken in Burgundy wine sauce"
        },
        {
          course: "Dessert",
          dish: "Crème Brûlée with Vanilla Bean",
          description: "Classic custard with caramelized sugar"
        }
      ]
    },
    {
      id: "italian-family",
      title: "Family Style Italian",
      description: "Authentic Italian recipes perfect for sharing",
      courses: [
        {
          course: "Antipasti",
          dish: "Charcuterie & Cheese Selection",
          description: "Imported meats, cheeses, and marinated vegetables"
        },
        {
          course: "Primi",
          dish: "Handmade Gnocchi Sorrentina",
          description: "Potato dumplings with San Marzano tomatoes and mozzarella"
        },
        {
          course: "Secondi",
          dish: "Osso Buco alla Milanese",
          description: "Braised veal shanks with saffron risotto"
        },
        {
          course: "Dolce",
          dish: "Tiramisu della Casa",
          description: "Traditional mascarpone dessert with espresso"
        }
      ]
    },
    {
      id: "asian-fusion",
      title: "Asian Fusion",
      description: "Modern interpretations of classic Asian dishes",
      courses: [
        {
          course: "Appetizer",
          dish: "Tuna Tataki with Sesame Crust",
          description: "Seared yellowfin tuna with wasabi aioli"
        },
        {
          course: "Soup",
          dish: "Tom Kha Gai",
          description: "Thai coconut chicken soup with galangal"
        },
        {
          course: "Main",
          dish: "Miso Glazed Sea Bass",
          description: "Broiled black cod with shiitake mushrooms and bok choy"
        },
        {
          course: "Dessert",
          dish: "Green Tea Panna Cotta",
          description: "Silky custard with red bean coulis"
        }
      ]
    },
    {
      id: "mexican-fiesta",
      title: "Mexican Celebration",
      description: "Vibrant Mexican flavors with premium ingredients",
      courses: [
        {
          course: "Aperitivo",
          dish: "Guacamole Trio with House-made Chips",
          description: "Traditional, spicy, and fruit-forward variations"
        },
        {
          course: "Entrada",
          dish: "Ceviche de Pescado",
          description: "Fresh fish marinated in citrus with avocado"
        },
        {
          course: "Plato Principal",
          dish: "Cochinita Pibil",
          description: "Slow-roasted pork with achiote and citrus"
        },
        {
          course: "Postre",
          dish: "Tres Leches with Cinnamon",
          description: "Traditional cake soaked in three milks"
        }
      ]
    },
    {
      id: "intimate-dinner",
      title: "Intimate Dinner for Two",
      description: "Romantic dining experience with aphrodisiac ingredients",
      courses: [
        {
          course: "Amuse Bouche",
          dish: "Oyster Shooter with Champagne",
          description: "Fresh oyster with bubbly and mignonette"
        },
        {
          course: "Appetizer",
          dish: "Seared Scallops with Truffle",
          description: "Pan-seared U-10 scallops with truffle oil"
        },
        {
          course: "Main",
          dish: "Filet Mignon with Red Wine Reduction",
          description: "Premium beef tenderloin with roasted asparagus"
        },
        {
          course: "Dessert",
          dish: "Chocolate Lava Cake",
          description: "Warm chocolate cake with vanilla ice cream"
        }
      ]
    },
    {
      id: "game-day",
      title: "Game Day / Poker Night",
      description: "Elevated comfort food perfect for entertaining",
      courses: [
        {
          course: "Snacks",
          dish: "Gourmet Sliders Station",
          description: "Mini wagyu burgers, pulled pork, and fried chicken"
        },
        {
          course: "Sides",
          dish: "Truffle Mac & Cheese",
          description: "Creamy cheese sauce with truffle oil and breadcrumbs"
        },
        {
          course: "Main",
          dish: "BBQ Ribs with House Sauce",
          description: "Fall-off-the-bone ribs with signature glaze"
        },
        {
          course: "Sweet",
          dish: "Bourbon Bread Pudding",
          description: "Warm dessert with vanilla bourbon sauce"
        }
      ]
    }
  ];

  return (
    <div className="page-services">
      {/* Hero Section */}
      <section className="hero-section bg-primary py-16">
        <div className="container text-center">
          <h1 className="hero-large mb-6">Services & Pricing</h1>
          <p className="body-large text-secondary max-w-2xl mx-auto">
            From intimate dinners to grand celebrations, I create personalized culinary 
            experiences that exceed expectations and create lasting memories.
          </p>
        </div>
      </section>

      {/* Pricing Information - Moved to top */}
      <section className="section-padding-small bg-accent">
        <div className="container text-center">
          <h2 className="heading-2 mb-6">Transparent Pricing Philosophy</h2>
          <div className="max-w-4xl mx-auto">
            <div className="bg-primary p-8 mb-6">
              <p className="body-large text-secondary mb-4">
                I believe in a transparent billing system. There are no hidden fees. 
                Clients are charged for the exact cost of ingredients plus a Chef's Service fee, 
                based on the complexity of the event.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-primary p-6 text-left">
                <h4 className="heading-3 mb-3 text-interactive-base">What You Pay:</h4>
                <ul className="space-y-2">
                  <li className="body-regular flex items-center space-x-2">
                    <span className="w-2 h-2 bg-interactive-base rounded-full"></span>
                    <span>100% of ingredient costs (with receipts provided)</span>
                  </li>
                  <li className="body-regular flex items-center space-x-2">
                    <span className="w-2 h-2 bg-interactive-base rounded-full"></span>
                    <span>Chef's service fee (based on event complexity)</span>
                  </li>
                  <li className="body-regular flex items-center space-x-2">
                    <span className="w-2 h-2 bg-interactive-base rounded-full"></span>
                    <span>Equipment and setup (if needed)</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-primary p-6 text-left">
                <h4 className="heading-3 mb-3 text-interactive-base">Included Services:</h4>
                <ul className="space-y-2">
                  <li className="body-regular flex items-center space-x-2">
                    <span className="w-2 h-2 bg-interactive-base rounded-full"></span>
                    <span>Menu planning and consultation</span>
                  </li>
                  <li className="body-regular flex items-center space-x-2">
                    <span className="w-2 h-2 bg-interactive-base rounded-full"></span>
                    <span>Shopping for ingredients</span>
                  </li>
                  <li className="body-regular flex items-center space-x-2">
                    <span className="w-2 h-2 bg-interactive-base rounded-full"></span>
                    <span>Cooking, plating, and service</span>
                  </li>
                  <li className="body-regular flex items-center space-x-2">
                    <span className="w-2 h-2 bg-interactive-base rounded-full"></span>
                    <span>Complete cleanup</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid - Moved below pricing */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4">Culinary Services</h2>
            <div className="w-16 h-1 bg-interactive-base mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {services.map(service => (
              <div key={service.id} className="bg-subtle p-8 hover-lift">
                <div className="text-interactive-base mb-4">
                  {service.icon}
                </div>
                
                <h3 className="heading-2 mb-4">{service.title}</h3>
                <p className="body-regular text-secondary mb-6">{service.description}</p>
                
                <div className="space-y-3 mb-6">
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-interactive-base rounded-full mt-2 flex-shrink-0"></div>
                      <span className="body-small">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Menus */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4">Sample Menus</h2>
            <p className="body-large text-secondary max-w-2xl mx-auto">
              Explore some of my signature menu collections. Each menu is fully customizable 
              to accommodate dietary restrictions and personal preferences.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {menuSamples.map(menu => (
                <AccordionItem key={menu.id} value={menu.id} className="border border-border-medium bg-primary">
                  <AccordionTrigger className="px-6 py-4 hover:bg-subtle">
                    <div className="text-left">
                      <h3 className="heading-3">{menu.title}</h3>
                      <p className="body-small text-secondary mt-1">{menu.description}</p>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <div className="space-y-4">
                      {menu.courses.map((course, index) => (
                        <div key={index} className="border-l-2 border-interactive-light pl-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <span className="body-small text-interactive-base font-medium uppercase tracking-wide">
                                {course.course}
                              </span>
                              <h4 className="body-regular font-medium mt-1">{course.dish}</h4>
                              <p className="body-small text-secondary mt-1">{course.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-subtle">
        <div className="container text-center">
          <h2 className="heading-1 mb-4">Ready to Plan Your Event?</h2>
          <p className="body-large text-secondary mb-8 max-w-xl mx-auto">
            Let's discuss your vision and create a custom menu that perfectly 
            matches your occasion and preferences.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="btn-primary">
              Get Your Custom Quote
            </a>
            <a href="/portfolio" className="btn-secondary">
              View Portfolio
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;